<template>
  <BaseModule
    v-bind="$props"
    class="background-text"
    data-module="background-text"
    :style="{
      backgroundColor,
      backgroundImage: bgImage,
      '--secondary-color': localSecondaryColor,
      '--primary-color': localPrimaryColor,
    }"
    :class="{
      'background-text--dark':
        filterType === 'dark' && backgroundType !== 'color',
      'background-text--light':
        filterType === 'light' && backgroundType !== 'color',
      'background-text--video': backgroundType === 'video',
    }"
  >
    <div class="background-text__video">
      <iframe
        v-if="!!video && isDesk"
        width="100%"
        height="100%"
        :src="video"
        frameborder="0"
        allowfullscreen
        allow="autoplay"
      >
      </iframe>
    </div>
    <div class="background-text__content">
      <header
        v-if="title"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
      </header>
      <div
        v-if="text"
        class="background-text__content__text base-content-fontsize"
        v-html="text"
      ></div>
      <div class="background-text__content__buttons base-content-fontsize">
        <EditorLink
          v-for="(button, index) in buttons"
          :key="index"
          :model="button.buttonUrl"
          class="cta"
          :class="{
            vazado: !button.buttonFeatured,
            dark: isPrimaryColorLight,
          }"
        >
          {{ button.buttonText }}
        </EditorLink>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import isObject from '@/helpers/isObject';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const tinycolor = require('tinycolor2');

export default {
  name: 'BackgroundText',
  components: {
    EditorLink,
    BaseModule,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    titleColor: {
      type: String,
      default: '#ffffff', // overrides default
    },
    textColor: {
      type: String,
      default: '#ffffff', // overrides default
    },
    backgroundType: {
      type: String,
      default: 'color',
    },
    backgroundImage: {
      type: String,
      default: '',
    },
    differentMobileBackgroundImage: {
      type: Boolean,
      default: false,
    },
    backgroundImageMobile: {
      type: String,
      default: '',
    },
    backgroundVideo: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    filterType: {
      type: String,
      default: 'dark',
    },
    text: {
      type: String,
      default: '',
    },
    localSecondaryColor: {
      type: String,
      default: '#FFFFFF',
    },
    localPrimaryColor: {
      type: String,
      default: '',
    },
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      innerWidth: window.innerWidth,
      mobileMaxWidth: 640,
      deskMinWidth: 1025,
    };
  },
  computed: {
    isDesk() {
      return this.innerWidth >= this.deskMinWidth;
    },
    video() {
      if (this.backgroundType === 'video' && this.backgroundVideo) {
        let urlValue = this.backgroundVideo;

        if (isObject(this.backgroundVideo)) {
          urlValue = this.backgroundVideo.value;
        }
        const regex =
          /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\\&v=)([^#\\&\\?]*).*/;
        const groups = regex.exec(urlValue);

        return groups !== null
          ? `https://www.youtube.com/embed/${groups[2]}?controls=0&showinfo=0&rel=0&autoplay=1&loop=1&playlist=${groups[2]}&start=0&mute=1`
          : '';
      }
      return null;
    },
    image() {
      if (this.backgroundType === 'image') {
        if (this.differentMobileBackgroundImage && !this.isDesk) {
          return this.backgroundImageMobile;
        }
        return this.backgroundImage;
      }
      if (
        this.backgroundType === 'video' &&
        !!this.backgroundVideo &&
        !this.isDesk
      ) {
        return this.backgroundImageMobile;
      }
      return null;
    },
    bgImage() {
      if (this.image) {
        return `url('${this.image}')`;
      }
      return '';
    },
    isPrimaryColorLight() {
      return (
        tinycolor.readability(
          this.localPrimaryColor || this.settings.primaryColor,
          '#FFFFFF',
        ) < 2
      );
    },
    ...mapGetters(['settings']),
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.innerWidth = window.innerWidth;
    });
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
            ],
          },
          {
            name: 'back',
            label: 'Fundo',
            fields: [
              {
                name: 'backgroundType',
                type: 'VueSelect',
                label: 'Tipo de fundo',
                options: [
                  {
                    label: 'Cor sólida',
                    value: 'color',
                  },
                  {
                    label: 'Imagem',
                    value: 'image',
                  },
                  {
                    label: 'Vídeo',
                    value: 'video',
                  },
                ],
              },
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'color';
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Preencha a cor de fundo',
                  },
                },
              },
              {
                name: 'backgroundVideo',
                type: 'Url',
                label: 'URL do vídeo de fundo',
                availableTypes: ['externalLink'],
                allowNewTab: false,
                isEnabled({ backgroundType }) {
                  return backgroundType === 'video';
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Informe a url do vídeo de fundo',
                  },
                },
              },
              {
                name: 'backgroundImage',
                type: 'ImageFile',
                label: 'Imagem de fundo',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'image';
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Insira uma imagem de fundo',
                  },
                },
              },
              {
                name: 'differentMobileBackgroundImage',
                type: 'Checkbox',
                label: 'Usar imagem diferente no mobile',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'image';
                },
              },
              {
                name: 'backgroundImageMobile',
                type: 'ImageFile',
                label: 'Imagem de fundo mobile',
                isEnabled({ backgroundType, differentMobileBackgroundImage }) {
                  return (
                    (backgroundType === 'image' &&
                      differentMobileBackgroundImage) ||
                    backgroundType === 'video'
                  );
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Insira uma imagem de fundo para ser exibida em dispositivos móveis',
                  },
                },
              },
              {
                name: 'filterType',
                type: 'VueSelect',
                label: 'Tipo de filtro',
                options: [
                  {
                    label: 'Nenhum',
                    value: '',
                  },
                  {
                    label: 'Escuro',
                    value: 'dark',
                  },
                  {
                    label: 'Claro',
                    value: 'light',
                  },
                ],
                isEnabled({ backgroundType }) {
                  return backgroundType !== 'color';
                },
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                name: 'buttons',
                type: 'List',
                maxItems: 3,
                itemLabel: 'Botão {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'localPrimaryColor',
                type: 'ColorPicker',
                label: 'Cor do botão destacado',
              },
              {
                name: 'buttonTextColor',
                after: 'localPrimaryColor',
                label: 'Cor do texto do botão destacado',
              },
              {
                name: 'localSecondaryColor',
                type: 'ColorPicker',
                label: 'Cor do botão vazado',
              },
            ],
            toRemove: ['backgroundColor', 'buttonColor'],
          },
        ],
      },
    ],
  },
};
</script>

<style lang="scss" scoped>
.background-text {
  position: relative;
  background-size: cover;
  z-index: 0;
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  &--video {
    @include media-breakpoint-up(sm) {
      min-height: 100vh;
      display: flex;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &--light {
    &::before {
      background: linear-gradient(
        148.09deg,
        rgba(255, 255, 255, 0.91) 21.66%,
        rgba(255, 255, 255, 0.72) 72.91%,
        rgba(255, 255, 255, 0) 98.53%
      );
      opacity: 0.85;
    }
  }

  &--dark {
    &::before {
      background: linear-gradient(
        148.09deg,
        rgba(0, 0, 0, 0.91) 21.66%,
        rgba(0, 0, 0, 0.72) 72.91%,
        rgba(0, 0, 0, 0) 98.53%
      );
      opacity: 0.75;
    }
  }

  &__video {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 0;

    iframe {
      width: 150%;
      height: 150%;
      position: absolute;
      top: -25%;
      right: 0;
      bottom: 0;
      left: -25%;
    }
  }

  &__content {
    padding: 0 34px;
    max-width: 823px + 34px * 2;
    margin: auto;
    text-align: center;
    position: relative;
    z-index: 2;

    h2 {
      color: var(--title-color);

      @include media-breakpoint-up(sm) {
        font-size: 50px;
      }
    }

    &__text {
      font-weight: 400;
      margin-bottom: 32px;
      color: var(--text-color);
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;

      @include media-breakpoint-up(sm) {
        margin-bottom: 54px;
      }
    }

    &__buttons {
      @include media-breakpoint-up(sm) {
        display: flex;
        justify-content: center;
      }

      .cta {
        max-width: 100%;
        margin: auto;

        @include media-breakpoint-up(sm) {
          margin: 0 20px;
        }

        &.vazado {
          color: var(--secondary-color);
          border-color: var(--secondary-color);

          @include media-breakpoint-down(md) {
            margin-bottom: 24px;
          }
        }
      }
    }

    > *:last-child {
      margin-bottom: 0 !important;
    }
  }
}
</style>
